import type { CampaignDraftCreatorItem } from '@/entities/campaign';
import { importCreatorsFromCsv } from '@/entities/campaign';
import { exportToCSV } from '@/shared/utils/export-to-csv';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback } from 'react';
import { toast } from 'sonner';

import {
  type CreatorCsvPreviewState,
  buildCampaignNameSuggestion,
  getUnsupportedPlatformCount,
  warnUnsupportedCampaignPlatforms,
  withFallbackCreatorRow,
} from '../../../../hooks';

type UseCampaignCreatorCsvImportArgs = {
  campaignItemSlotLimit: number;
  draftCreatorItems: CampaignDraftCreatorItem[];
  remainingSlots: number;
  resetCreatorValidation: () => void;
  setCreatorItems: Dispatch<SetStateAction<CampaignDraftCreatorItem[]>>;
};

export function useCampaignCreatorCsvImport({
  campaignItemSlotLimit,
  draftCreatorItems,
  remainingSlots,
  resetCreatorValidation,
  setCreatorItems,
}: UseCampaignCreatorCsvImportArgs) {
  const downloadCreatorTemplate = useCallback(() => {
    exportToCSV(
      [
        'source,platform,label',
        'synclycreator,tiktok,Gifting',
      ].join('\n'),
      'campaign-creator-template.csv',
    );
  }, []);

  const previewCreatorCsvImport = useCallback(
    async (file: File) => {
      const result = importCreatorsFromCsv({
        text: await file.text(),
        fileName: file.name,
        existingRows: draftCreatorItems,
        availableSlots: remainingSlots,
        itemLimit: campaignItemSlotLimit,
      });

      const limitSkippedCount = result.summary.errors.filter((error) =>
        error.message.includes('campaign limit'),
      ).length;
      const unsupportedPlatformCount = getUnsupportedPlatformCount(result.summary);

      return {
        rows: result.rows,
        summary: result.summary,
        suggestedCampaignName: buildCampaignNameSuggestion(file.name),
        limitSkippedCount,
        unsupportedPlatformCount,
        validationErrorCount:
          result.summary.errors.length - limitSkippedCount - unsupportedPlatformCount,
      };
    },
    [campaignItemSlotLimit, draftCreatorItems, remainingSlots],
  );

  const applyCreatorCsvImport = useCallback(
    (preview: CreatorCsvPreviewState) => {
      const unsupportedPlatformWarning = warnUnsupportedCampaignPlatforms(
        preview.unsupportedPlatformCount,
      );
      if (preview.summary.added === 0) return;
      resetCreatorValidation();
      setCreatorItems(withFallbackCreatorRow(preview.rows));
      if (unsupportedPlatformWarning) {
        toast.warning(unsupportedPlatformWarning);
      }
    },
    [resetCreatorValidation, setCreatorItems],
  );

  return {
    applyCreatorCsvImport,
    downloadCreatorTemplate,
    previewCreatorCsvImport,
  };
}
